import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Dialog, FieldShell, Input, Textarea } from '../../design'
import { createStory, type Story, type StoryPriority, type StoryType } from '../../api/stories'
import { lookupEpics, lookupUsers } from '../../api/users'
import { useAuth } from '../../auth/AuthContext'

const selectClass =
  'w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg ' +
  'focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent'

/**
 * The header "New story" button's form. Only the fields worth setting up
 * front — everything else is edited in the StoryDrawer the caller opens next.
 */
export function NewStoryDialog({
  projectId,
  open,
  onOpenChange,
  onCreated,
}: {
  projectId: number
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (story: Story) => void
}) {
  const qc = useQueryClient()
  const { user } = useAuth()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [type, setType] = useState<StoryType>('story')
  const [priority, setPriority] = useState<StoryPriority>('med')
  const [epicId, setEpicId] = useState('')
  const [assigneeId, setAssigneeId] = useState(user ? String(user.id) : '')
  const [plannedStart, setPlannedStart] = useState('')
  const [dueDate, setDueDate] = useState('')

  const epicsQ = useQuery({
    queryKey: ['lookups', 'epics', projectId],
    queryFn: () => lookupEpics(projectId),
    staleTime: 60_000,
    enabled: open,
  })
  const usersQ = useQuery({
    queryKey: ['lookups', 'users'],
    queryFn: lookupUsers,
    staleTime: 60_000,
    enabled: open,
  })

  function reset() {
    setTitle('')
    setDescription('')
    setType('story')
    setPriority('med')
    setEpicId('')
    setAssigneeId(user ? String(user.id) : '')
    setPlannedStart('')
    setDueDate('')
    createMut.reset()
  }

  const createMut = useMutation({
    mutationFn: () =>
      createStory({
        project_id: projectId,
        title: title.trim(),
        description: description.trim() || undefined,
        type,
        priority,
        epic_id: epicId ? Number(epicId) : undefined,
        assignee_id: assigneeId ? Number(assigneeId) : undefined,
        planned_start: plannedStart || undefined,
        due_date: dueDate || undefined,
      } as Parameters<typeof createStory>[0]),
    onSuccess: story => {
      qc.invalidateQueries({ queryKey: ['stories'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      reset()
      onOpenChange(false)
      onCreated(story)
    },
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!title.trim() || createMut.isPending) return
    createMut.mutate()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) reset()
        onOpenChange(next)
      }}
      title="New story"
    >
      <form onSubmit={onSubmit} className="space-y-3">
        <Input
          id="new-story-title"
          label="Title"
          required
          autoFocus
          value={title}
          onChange={e => setTitle(e.target.value)}
          maxLength={500}
          placeholder="What needs doing?"
        />
        <Textarea
          id="new-story-description"
          label="Description"
          rows={3}
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Optional — markdown supported"
        />
        <div className="grid grid-cols-2 gap-3">
          <FieldShell label="Type" htmlFor="new-story-type">
            <select id="new-story-type" className={selectClass} value={type}
              onChange={e => setType(e.target.value as StoryType)}>
              <option value="story">Story</option>
              <option value="task">Task</option>
              <option value="bug">Bug</option>
            </select>
          </FieldShell>
          <FieldShell label="Priority" htmlFor="new-story-priority">
            <select id="new-story-priority" className={selectClass} value={priority}
              onChange={e => setPriority(e.target.value as StoryPriority)}>
              <option value="low">Low</option>
              <option value="med">Medium</option>
              <option value="high">High</option>
            </select>
          </FieldShell>
          <FieldShell label="Assignee" htmlFor="new-story-assignee">
            <select id="new-story-assignee" className={selectClass} value={assigneeId}
              onChange={e => setAssigneeId(e.target.value)}>
              <option value="">Unassigned</option>
              {(usersQ.data ?? []).map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </FieldShell>
          <FieldShell label="Epic" htmlFor="new-story-epic">
            <select id="new-story-epic" className={selectClass} value={epicId}
              onChange={e => setEpicId(e.target.value)}>
              <option value="">No epic</option>
              {(epicsQ.data ?? []).map(ep => (
                <option key={ep.id} value={ep.id}>{ep.name}</option>
              ))}
            </select>
          </FieldShell>
          <Input
            id="new-story-planned-start"
            label="Planned start"
            type="date"
            value={plannedStart}
            onChange={e => setPlannedStart(e.target.value)}
          />
          <Input
            id="new-story-due"
            label="Due date"
            type="date"
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
          />
        </div>

        {createMut.error && (
          <p className="text-xs text-danger">{(createMut.error as Error).message}</p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" size="sm" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={!title.trim() || createMut.isPending}>
            {createMut.isPending ? 'Creating…' : 'Create story'}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
